import { KeyboardArrowDown as KeyboardArrowDownIcon } from 'components/icons';
import EmptyState from 'components/EmptyState';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Separator } from 'components/ui/separator';
import React, { useMemo, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { QuickFilterDropdownMultiselectForm, SelectOption } from '../../ListTableTypes';

interface QuickFilterDropdownProps {
    filter: QuickFilterDropdownMultiselectForm;
    onChange: (selected: SelectOption[], selector: string, comperator?: string) => void;
}

const QuickFilterDropdown: React.FC<QuickFilterDropdownProps> = ({ filter, onChange }) => {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchRef = useRef<HTMLInputElement | null>(null);

    const hasSelection = (filter.selected?.length || 0) > 0;

    // The options and the selected labels keep the filter's own option order, so an option does not move
    // under the pointer when it is ticked and the label reads the same whatever order the clicks came in.
    const buttonLabel = useMemo(() => {
        if (!hasSelection) {
            return filter.label;
        }
        const selectedValues = new Set(filter.selected.map((s) => s.value));
        const inOptionOrder = filter.options.filter((opt) => selectedValues.has(opt.value));
        const unlisted = filter.selected.filter((s) => !filter.options.some((opt) => opt.value === s.value));
        return [...inOptionOrder, ...unlisted].map((opt) => opt.label).join(', ');
    }, [filter.label, filter.options, filter.selected, hasSelection]);

    const filteredOptions = useMemo(() => {
        if (!searchValue.trim()) {
            return filter.options;
        }
        const search = searchValue.toLowerCase();
        return filter.options.filter((opt) => opt.label.toLowerCase().includes(search));
    }, [filter.options, searchValue]);

    const handleToggleOption = (option: SelectOption) => {
        const isSelected = filter.selected?.some((s) => s.value === option.value);
        let newSelected: SelectOption[];
        if (isSelected) {
            newSelected = filter.selected.filter((s) => s.value !== option.value);
        } else {
            newSelected = [...(filter.selected || []), option];
        }
        onChange(newSelected, filter.selector, filter.comperator);
    };

    const handleSelectAll = () => {
        onChange(filter.options, filter.selector, filter.comperator);
    };

    const handleClearAll = () => {
        onChange([], filter.selector, filter.comperator);
    };

    const isOptionSelected = (option: SelectOption) => {
        return filter.selected?.some((s) => s.value === option.value) || false;
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearchValue('');
            }}
        >
            <PopoverTrigger
                render={
                    <Button
                        variant="outline"
                        className={cn(
                            // A selection shows as the brand green border and text, with no fill.
                            'text-meta h-8 max-w-[200px] justify-start gap-1 bg-transparent px-3 shadow-none',
                            hasSelection
                                ? 'border-primary text-primary hover:text-primary aria-expanded:text-primary font-semibold hover:bg-transparent aria-expanded:bg-transparent'
                                : 'border-divider text-fg-secondary hover:border-primary hover:text-fg-secondary aria-expanded:text-fg-secondary font-normal'
                        )}
                    />
                }
            >
                <span className="truncate">{buttonLabel}</span>
                <KeyboardArrowDownIcon className="size-5 shrink-0" />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={4}
                initialFocus={searchRef}
                // A dropdown, stacked with the others above page chrome.
                positionerClassName="z-(--z-dropdown)"
                className="border-divider flex max-h-[400px] w-[280px] flex-col gap-0 rounded-md border p-0 shadow-lg ring-0"
            >
                <div className="border-divider border-b p-2">
                    <Input
                        ref={searchRef}
                        className="h-8 text-base md:text-sm"
                        placeholder={t('search')}
                        aria-label={filter.label}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </div>
                <div className="border-divider flex items-center gap-2 border-b px-2 py-1">
                    <Button variant="link" size="xs" className="text-link text-xs" onClick={handleSelectAll}>
                        {t('forms.select-all')}
                    </Button>
                    <Separator orientation="vertical" className="h-5" />
                    <Button variant="link" size="xs" className="text-link text-xs" onClick={handleClearAll}>
                        {t('clear')}
                    </Button>
                </div>
                <div className="flex-1 overflow-y-auto py-1">
                    {filteredOptions.length === 0 ? (
                        <EmptyState size="inline" titleId="no-results" surface="quick-filter-dropdown" />
                    ) : (
                        filteredOptions.map((option) => (
                            <label
                                key={String(option.value)}
                                className="hover:bg-muted flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm select-none"
                            >
                                <Checkbox
                                    variant="secondary"
                                    checked={isOptionSelected(option)}
                                    onCheckedChange={() => handleToggleOption(option)}
                                />
                                {option.label}
                            </label>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default QuickFilterDropdown;
